// ============================================================================
// Vista — Panel Super Admin: Configuración (/configuracion)
//
// Muestra la gestión de pagos (configuración global + métodos de pago
// dinámicos) y conserva la tabla de parámetros de plataforma existente.
// ============================================================================

import { crearTabla, mostrarToast } from '@plataforma/ui';

import {
  eliminarMetodoPago,
  gestionarQrPago,
  guardarConfigPagos,
  guardarMetodoPago,
  listarConfiguracionPlataforma,
  listarMetodosPago,
  obtenerConfigPagos,
  reordenarMetodosPago,
} from '../api/backoffice.js';
import { exigirSuperAdmin } from '../guardas.js';

import { crearBadge, estadoCarga, estadoError, fechaHora } from './backoffice-ui.js';
import { montarShell } from './shell.js';

const TIPOS_METODO = {
  billetera: { etiqueta: 'Billetera', tono: 'info' },
  banco: { etiqueta: 'Banco', tono: 'exito' },
};

export async function vistaConfiguracion(contenedorRaiz) {
  if (!exigirSuperAdmin()) return;

  const contenido = montarShell(contenedorRaiz, '/configuracion');
  contenido.innerHTML = `
    <div class="pf-backoffice-header">
      <div>
        <h1>Configuración</h1>
        <p class="pf-texto-suave">Parámetros de la plataforma y métodos de pago.</p>
      </div>
    </div>

    <div class="pf-tarjeta" style="margin-bottom: 1.5rem;">
      <h2 style="margin:0 0 0.25rem; font-size:1.05rem;">Configuración de pagos</h2>
      <p class="pf-texto-suave" style="margin:0 0 1rem;">Titular general que se muestra de forma destacada en la pantalla de pago del onboarding.</p>
      <div class="pf-backoffice-detail">
        <div class="pf-campo">
          <label for="pf-titular-general">Titular general de pagos</label>
          <input id="pf-titular-general" placeholder="Ej. Juan Kevin Ramos Yaranga" />
        </div>
      </div>
      <div class="pf-formulario__footer" style="padding:0; border:none; background:transparent;">
        <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-guardar-config">Guardar configuración</button>
      </div>
    </div>

    <div class="pf-tarjeta">
      <div class="pf-backoffice-header" style="padding:0 0 1rem; border:none;">
        <div>
          <h2 style="margin:0 0 0.25rem; font-size:1.05rem;">Métodos de pago</h2>
          <p class="pf-texto-suave" style="margin:0;">Los usuarios del onboarding solo ven los métodos activos, en este orden.</p>
        </div>
        <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-nuevo-metodo">Agregar método</button>
      </div>
      <div id="pf-metodos-tabla">${estadoCarga()}</div>
    </div>

    <div class="pf-tarjeta" style="margin-top:1.5rem;">
      <h2 style="margin:0 0 1rem; font-size:1.05rem;">Parámetros de plataforma</h2>
      <div id="pf-configuracion-tabla">${estadoCarga()}</div>
    </div>

    <pf-modal titulo="Método de pago" id="pf-modal-metodo"></pf-modal>
    <pf-modal titulo="Eliminar método de pago" id="pf-modal-eliminar"></pf-modal>
  `;

  contenido
    .querySelector('#pf-btn-guardar-config')
    .addEventListener('click', () => guardarConfiguracionPagos(contenido));
  contenido
    .querySelector('#pf-btn-nuevo-metodo')
    .addEventListener('click', () => abrirFormularioMetodo(contenido, null));

  await Promise.all([
    cargarConfiguracionPagos(contenido),
    cargarMetodosPago(contenido),
    cargarConfiguracionPlataforma(contenido),
  ]);
}

// ----------------------------------------------------------------------------
// Configuración global
// ----------------------------------------------------------------------------

async function cargarConfiguracionPagos(contenido) {
  const input = contenido.querySelector('#pf-titular-general');
  const boton = contenido.querySelector('#pf-btn-guardar-config');
  boton.disabled = true;

  const { data, error } = await obtenerConfigPagos();
  if (!error && data) {
    input.value = data.titular_general ?? '';
  }
  boton.disabled = false;
}

async function guardarConfiguracionPagos(contenido) {
  const boton = contenido.querySelector('#pf-btn-guardar-config');
  const titular = contenido.querySelector('#pf-titular-general').value.trim();

  if (!titular) {
    mostrarToast('El titular general es obligatorio.', { tono: 'peligro' });
    return;
  }

  boton.disabled = true;
  const { error } = await guardarConfigPagos(titular);
  boton.disabled = false;

  if (error) {
    mostrarToast(error.message || 'No se pudo guardar la configuración.', { tono: 'peligro' });
    return;
  }

  mostrarToast('Configuración de pagos guardada.', { tono: 'exito' });
}

// ----------------------------------------------------------------------------
// Métodos de pago
// ----------------------------------------------------------------------------

async function cargarMetodosPago(contenido) {
  const destino = contenido.querySelector('#pf-metodos-tabla');
  destino.innerHTML = estadoCarga();

  const { data, error } = await listarMetodosPago();
  if (error) {
    destino.innerHTML = estadoError('No se pudieron cargar los métodos de pago.');
    return;
  }

  const métodos = data ?? [];
  const tabla = crearTabla({
    filaVacia: 'No hay métodos de pago registrados.',
    columnas: [
      {
        titulo: 'Método',
        render: (fila) => fila.nombre || fila.slug,
      },
      { titulo: 'Tipo', render: (fila) => crearBadge(TIPOS_METODO, fila.tipo) },
      {
        titulo: 'Estado',
        render: (fila) =>
          crearBadge(
            {
              true: { etiqueta: 'Activo', tono: 'exito' },
              false: { etiqueta: 'Inactivo', tono: 'neutral' },
            },
            String(fila.activo),
          ),
      },
      { titulo: 'Orden', render: (fila) => fila.orden ?? 0 },
      {
        titulo: 'Acciones',
        render: (fila) => {
          const contenedor = document.createElement('div');
          contenedor.className = 'pf-backoffice-actions';

          const indice = métodos.findIndex((m) => m.id === fila.id);
          contenedor.appendChild(
            hacerBoton('Subir', indice <= 0, () => moverMetodo(contenido, métodos, indice, -1)),
          );
          contenedor.appendChild(
            hacerBoton('Bajar', indice >= métodos.length - 1, () =>
              moverMetodo(contenido, métodos, indice, 1),
            ),
          );
          contenedor.appendChild(
            hacerBoton('Editar', false, () => abrirFormularioMetodo(contenido, fila)),
          );
          contenedor.appendChild(
            hacerBoton(fila.activo ? 'Desactivar' : 'Activar', false, () =>
              toggleActivoMetodo(contenido, fila),
            ),
          );
          contenedor.appendChild(
            hacerBoton('Eliminar', false, () => abrirConfirmarEliminar(contenido, fila), 'peligro'),
          );

          return contenedor;
        },
      },
    ],
    filas: métodos,
  });

  destino.innerHTML = '';
  destino.appendChild(tabla);
}

function hacerBoton(texto, deshabilitado, onClick, tono = 'secundario') {
  const boton = document.createElement('button');
  boton.type = 'button';
  boton.className = `pf-boton pf-boton--${tono} pf-boton--chico`;
  boton.textContent = texto;
  boton.disabled = deshabilitado;
  boton.addEventListener('click', onClick);
  return boton;
}

async function moverMetodo(contenido, métodos, indice, delta) {
  const destino = indice + delta;
  if (destino < 0 || destino >= métodos.length) return;

  const copia = métodos.slice();
  const [elem] = copia.splice(indice, 1);
  copia.splice(destino, 0, elem);

  const { error } = await reordenarMetodosPago(copia.map((m) => m.id));
  if (error) {
    mostrarToast(error.message || 'No se pudo reordenar.', { tono: 'peligro' });
    return;
  }
  mostrarToast('Orden actualizado.', { tono: 'exito' });
  await cargarMetodosPago(contenido);
}

async function toggleActivoMetodo(contenido, fila) {
  const { error } = await guardarMetodoPago({
    ...fila,
    activo: !fila.activo,
  });
  if (error) {
    mostrarToast(error.message || 'No se pudo cambiar el estado.', { tono: 'peligro' });
    return;
  }
  mostrarToast(fila.activo ? 'Método desactivado.' : 'Método activado.', { tono: 'exito' });
  await cargarMetodosPago(contenido);
}

// ----------------------------------------------------------------------------
// Formulario agregar / editar método (se adapta al tipo)
// ----------------------------------------------------------------------------

function abrirFormularioMetodo(contenido, metodo) {
  const modal = contenido.querySelector('#pf-modal-metodo');
  const esBilletera = metodo?.tipo === 'billetera';
  const esBanco = metodo?.tipo === 'banco';

  modal.setAttribute('titulo', metodo ? `Editar: ${metodo.nombre}` : 'Agregar método');
  modal.innerHTML = `
    <form id="pf-form-metodo">
      <div class="pf-backoffice-detail">
        <div class="pf-campo">
          <label for="pf-m-nombre">Nombre</label>
          <input id="pf-m-nombre" value="${metodo?.nombre ?? ''}" placeholder="Ej. Yape" required />
        </div>
        <div class="pf-campo">
          <label for="pf-m-slug">Slug</label>
          <input id="pf-m-slug" value="${metodo?.slug ?? ''}" placeholder="Ej. yape" required />
        </div>
        <div class="pf-campo">
          <label for="pf-m-tipo">Tipo</label>
          <select id="pf-m-tipo" required>
            <option value="billetera" ${esBilletera || !metodo ? 'selected' : ''}>Billetera</option>
            <option value="banco" ${esBanco ? 'selected' : ''}>Banco</option>
          </select>
        </div>

        <div class="pf-campo" id="pf-grupo-numero">
          <label for="pf-m-numero">Número de billetera</label>
          <input id="pf-m-numero" value="${metodo?.numero ?? ''}" placeholder="Ej. 999 999 999" />
        </div>

        <div class="pf-campo" id="pf-grupo-cci" hidden>
          <label for="pf-m-cci">CCI</label>
          <input id="pf-m-cci" value="${metodo?.cci ?? ''}" placeholder="Código de cuenta interbancaria" />
        </div>

        <div class="pf-campo" id="pf-grupo-qr">
          <label for="pf-m-qr">Código QR</label>
          <div id="pf-m-qr-preview" style="margin-bottom:0.5rem;"></div>
          <input type="file" id="pf-m-qr" accept="image/jpeg,image/png,image/webp" />
          <small>Sube una imagen PNG/JPG/WEBP (máx. 2 MB).</small>
        </div>

        <div class="pf-campo">
          <label for="pf-m-instrucciones">Instrucciones</label>
          <textarea id="pf-m-instrucciones" rows="3" placeholder="Pasos para completar el pago...">${metodo?.instrucciones ?? ''}</textarea>
        </div>

        <div class="pf-campo">
          <label for="pf-m-orden">Orden</label>
          <input id="pf-m-orden" type="number" min="0" value="${metodo?.orden ?? 0}" required />
        </div>

        <div class="pf-campo">
          <label for="pf-m-titular">Titular personalizado (opcional)</label>
          <input id="pf-m-titular" value="${metodo?.titular_personalizado ?? ''}" placeholder="Si está vacío, se usa el titular general" />
        </div>

        <div class="pf-campo">
          <label class="pf-interruptor" for="pf-m-activo">
            <input type="checkbox" id="pf-m-activo" ${metodo?.activo !== false ? 'checked' : ''} />
            <span class="pf-interruptor__carril"></span>
            Activo
          </label>
        </div>
      </div>
      <p class="pf-campo__error" id="pf-m-error" hidden></p>
      <div class="pf-formulario__footer">
        <button type="button" class="pf-boton pf-boton--secundario" id="pf-m-cancelar">Cancelar</button>
        <button type="submit" class="pf-boton pf-boton--primario">Guardar</button>
      </div>
    </form>
  `;

  const selectTipo = modal.querySelector('#pf-m-tipo');
  const grupoNumero = modal.querySelector('#pf-grupo-numero');
  const grupoCci = modal.querySelector('#pf-grupo-cci');
  const grupoQr = modal.querySelector('#pf-grupo-qr');
  const inputQr = modal.querySelector('#pf-m-qr');
  const preview = modal.querySelector('#pf-m-qr-preview');
  const qrActual = metodo?.qr_url ?? null;

  function sincronizarTipo() {
    const tipo = selectTipo.value;
    const esB = tipo === 'billetera';
    const labelNumero = grupoNumero.querySelector('label');
    labelNumero.textContent = esB ? 'Número de billetera' : 'Número de cuenta';
    grupoCci.hidden = esB;
    // QR solo para billeteras
    grupoQr.style.display = esB ? '' : 'none';
  }

  function renderizarPreview() {
    preview.innerHTML = '';
    if (qrActual) {
      const img = document.createElement('img');
      img.src = qrActual;
      img.alt = 'QR actual';
      img.style.cssText =
        'width:120px;height:120px;object-fit:contain;border:1px solid var(--pf-color-borde,#e2e8f0);border-radius:8px;';
      preview.appendChild(img);
    } else {
      preview.textContent = '';
    }
  }

  selectTipo.addEventListener('change', sincronizarTipo);
  modal.querySelector('#pf-m-cancelar').addEventListener('click', () => modal.cerrar());

  inputQr.addEventListener('change', async () => {
    const file = inputQr.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      mostrarToast('La imagen supera los 2 MB.', { tono: 'peligro' });
      inputQr.value = '';
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      mostrarToast('Formato no válido. Usa PNG, JPG o WEBP.', { tono: 'peligro' });
      inputQr.value = '';
      return;
    }

    // Vista previa antes de guardar
    const reader = new FileReader();
    reader.onload = () => {
      preview.innerHTML = '';
      const img = document.createElement('img');
      img.src = String(reader.result);
      img.alt = 'Vista previa QR';
      img.style.cssText =
        'width:120px;height:120px;object-fit:contain;border:1px solid var(--pf-color-borde,#e2e8f0);border-radius:8px;';
      preview.appendChild(img);
    };
    reader.readAsDataURL(file);
  });

  modal.querySelector('#pf-form-metodo').addEventListener('submit', async (evento) => {
    evento.preventDefault();
    const errorEl = modal.querySelector('#pf-m-error');
    errorEl.hidden = true;

    const file = inputQr.files?.[0] ?? null;

    const datos = {
      id: metodo?.id ?? null,
      nombre: modal.querySelector('#pf-m-nombre').value.trim(),
      slug: modal.querySelector('#pf-m-slug').value.trim(),
      tipo: selectTipo.value,
      numero: modal.querySelector('#pf-m-numero').value.trim(),
      cci: selectTipo.value === 'banco' ? modal.querySelector('#pf-m-cci').value.trim() : '',
      instrucciones: modal.querySelector('#pf-m-instrucciones').value.trim(),
      orden: Number(modal.querySelector('#pf-m-orden').value),
      activo: modal.querySelector('#pf-m-activo').checked,
      titular_personalizado: modal.querySelector('#pf-m-titular').value.trim(),
      qr_url: qrActual,
    };

    if (!datos.nombre || !datos.slug) {
      errorEl.textContent = 'Nombre y slug son obligatorios.';
      errorEl.hidden = false;
      return;
    }

    const botonGuardar = modal.querySelector('button[type="submit"]');
    botonGuardar.disabled = true;
    botonGuardar.textContent = 'Guardando...';

    try {
      // Si hay un QR nuevo, subirlo primero
      if (file) {
        const reader = new FileReader();
        const base64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const { data: qr, error: qrError } = await gestionarQrPago('subir', {
          base64,
          mime: file.type,
          nombre: file.name,
          ruta_existente: qrActual || undefined,
        });

        if (qrError || !qr?.url) {
          errorEl.textContent = qrError?.message || 'No se pudo subir el QR.';
          errorEl.hidden = false;
          botonGuardar.disabled = false;
          botonGuardar.textContent = 'Guardar';
          return;
        }
        datos.qr_url = qr.url;
      }

      const { error } = await guardarMetodoPago(datos);
      if (error) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
        botonGuardar.disabled = false;
        botonGuardar.textContent = 'Guardar';
        return;
      }

      mostrarToast('Método de pago guardado.', { tono: 'exito' });
      modal.cerrar();
      await cargarMetodosPago(contenido);
      await cargarConfiguracionPagos(contenido);
    } catch {
      errorEl.textContent = 'Ocurrió un error inesperado.';
      errorEl.hidden = false;
      botonGuardar.disabled = false;
      botonGuardar.textContent = 'Guardar';
    }
  });

  sincronizarTipo();
  renderizarPreview();
  modal.abrir();
}

function abrirConfirmarEliminar(contenido, metodo) {
  const modal = contenido.querySelector('#pf-modal-eliminar');
  modal.setAttribute('titulo', 'Eliminar método de pago');
  modal.innerHTML = `
    <form id="pf-form-eliminar">
      <p>¿Eliminar el método <strong>${metodo.nombre}</strong>? Esta acción no se puede deshacer.</p>
      <div class="pf-campo">
        <label for="pf-e-motivo">Motivo</label>
        <textarea id="pf-e-motivo" rows="2" required placeholder="Indica el motivo de la eliminación"></textarea>
      </div>
      <p class="pf-campo__error" id="pf-e-error" hidden></p>
      <div class="pf-formulario__footer">
        <button type="button" class="pf-boton pf-boton--secundario" id="pf-e-cancelar">Cancelar</button>
        <button type="submit" class="pf-boton pf-boton--peligro">Eliminar</button>
      </div>
    </form>
  `;

  modal.querySelector('#pf-e-cancelar').addEventListener('click', () => modal.cerrar());
  modal.querySelector('#pf-form-eliminar').addEventListener('submit', async (evento) => {
    evento.preventDefault();
    const errorEl = modal.querySelector('#pf-e-error');
    errorEl.hidden = true;
    const motivo = modal.querySelector('#pf-e-motivo').value.trim();

    if (!motivo) {
      errorEl.textContent = 'El motivo es obligatorio.';
      errorEl.hidden = false;
      return;
    }

    // Limpieza best-effort del QR en Storage
    if (metodo.qr_url) {
      await gestionarQrPago('borrar', { ruta: metodo.qr_url });
    }

    const { error } = await eliminarMetodoPago(metodo.id, motivo);
    if (error) {
      errorEl.textContent = error.message;
      errorEl.hidden = false;
      return;
    }

    mostrarToast('Método de pago eliminado.', { tono: 'exito' });
    modal.cerrar();
    await cargarMetodosPago(contenido);
  });

  modal.abrir();
}

// ----------------------------------------------------------------------------
// Parámetros de plataforma (se conserva la tabla previa)
// ----------------------------------------------------------------------------

async function cargarConfiguracionPlataforma(contenido) {
  const destino = contenido.querySelector('#pf-configuracion-tabla');
  destino.innerHTML = estadoCarga();

  const { data, error } = await listarConfiguracionPlataforma();
  if (error) {
    destino.innerHTML = estadoError('No se pudo cargar la configuración.');
    return;
  }

  destino.innerHTML = '';
  destino.appendChild(
    crearTabla({
      filaVacia: 'No hay parámetros de configuración registrados.',
      columnas: [
        { clave: 'clave', titulo: 'Clave' },
        { titulo: 'Valor', render: (fila) => JSON.stringify(fila.valor) },
        { titulo: 'Descripción', render: (fila) => fila.descripcion ?? '—' },
        { titulo: 'Actualización', render: (fila) => fechaHora(fila.actualizado_en) },
      ],
      filas: data ?? [],
    }),
  );
}
